import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
//Read all the comments carefully in this slice before understanding the updation process
const initialState = {
  selectedDataCollectionBrands: null,
  page: 0,
  rowsPerPage: 10,
  selectedRows: [],
  openModalType: null,
  selectedBrands: null,
  selectedAccount: null,
  selectedCompany: null,
  selectedWebsites: null,
  selectedStatus: null,
  selectedRawTableId: null,
  selectedInterface: null,
  isModalOpen: false, //It stores whether modal is open or not
  brandDataInModal: [] as any, //It stores selected brand's brandId, brandName, row_output_table_data (It is a array of objects which contains config_name and table_id)
  startDate: dayjs().startOf("month").format("YYYY-MM-DD"), //It stores startdate from update modal
  endDate: dayjs().format("YYYY-MM-DD"),
  //It stores enddate from update modal
  selectedAction: null, //It stores action name from update modal (Whether it is fetcher or unifier or end to end sync)
  selectedActionId: null, //It stores action id (which is required for API call) from update modal (Whether it is fetcher or unifier or end to end sync)
  sort: {
    keytoSort: "modified",
    direction: "desc",
  },
  dataCollectionBrandsList: [], //It stores data collection brands list after manually merging
  selectedBrandConfigs: [], // It stores selected brand's (when we go for fetcher) brandId, brandName, selectedConfigs (It is a array of objects which contains config_name and table_id), selectedBrandConfigs is used to store the selected brand's config data when we go for fetcher
  selectedUnifierDestinationTableId: [],
  singleRowData: [],
  resWorkflowName: null,
  isLoading: false,
  isSuccess: false,
};

const dataCollectionBrandsSlice = createSlice({
  name: "dataCollectionBrands",
  initialState,
  reducers: {
    setSelectedDataCollectionBrands: (state, action) => {
      state.selectedDataCollectionBrands = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setSelectedRows: (state, action) => {
      state.selectedRows = action.payload;
    },
    setOpenModalType: (state, action) => {
      state.openModalType = action.payload;
    },
    setSelectedBrands: (state, action) => {
      state.selectedBrands = action.payload;
    },
    setSelectedAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
    setSelectedCompany: (state, action) => {
      state.selectedCompany = action.payload;
    },
    setSelectedWebsites: (state, action) => {
      state.selectedWebsites = action.payload;
    },
    setSelectedStatus: (state, action) => {
      state.selectedStatus = action.payload;
    },
    setSelectedRawTableId: (state, action) => {
      state.selectedRawTableId = action.payload;
    },
    setSelectedInterface: (state, action) => {
      state.selectedInterface = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setDataCollectionBrandsList: (state, action) => {
      state.dataCollectionBrandsList = action.payload;
    },
    setSingleRowData: (state, action) => {
      state.singleRowData = action.payload;
    },
    setResWorkflowName: (state, action) => {
      state.resWorkflowName = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsSuccess: (state, action) => {
      state.isSuccess = action.payload;
    },
    setModalOpen: (state, action) => {
      state.isModalOpen = action.payload.isOpen;
      const data = action.payload.data;
      state.brandDataInModal = Array.isArray(data) ? data : data ? [data] : [];
      if (action.payload.isOpen) {
        state.startDate = dayjs().startOf("month").format("YYYY-MM-DD");
        state.endDate = dayjs().format("YYYY-MM-DD");
      }
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },
    setSelectedAction(state, action) {
      state.selectedAction = action.payload;
    },
    setSelectedActionId(state, action) {
      state.selectedActionId = action.payload;
    },
    setSelectedBrandConfigs: (state, action) => {
      if (Array.isArray(action.payload)) {
        //@ts-ignore
        state.selectedBrandConfigs = action.payload;
        return;
      }
      //We are making the Data Structure Here for fetcher configs
      const { brandId, brandName, configName, tableId, checked } =
        action.payload;

      if (!state.selectedBrandConfigs) {
        state.selectedBrandConfigs = [];
      }

      const brandIndex = state.selectedBrandConfigs.findIndex(
        (b: any) => b.brandId === brandId
      );

      if (brandIndex === -1 && checked) {
        // @ts-ignore
        state.selectedBrandConfigs.push({
          brandId,
          brandName,
          selectedConfigs: [{ configName, tableId }],
        });
      } else if (brandIndex !== -1) {
        const brand: any = state.selectedBrandConfigs[brandIndex];
        const configIndex = brand.selectedConfigs.findIndex(
          (c: any) => c.tableId === tableId
        );

        if (checked && configIndex === -1) {
          brand.selectedConfigs.push({ configName, tableId });
        } else if (!checked && configIndex !== -1) {
          brand.selectedConfigs.splice(configIndex, 1);
          if (brand.selectedConfigs.length === 0) {
            state.selectedBrandConfigs.splice(brandIndex, 1);
          }
        }
      }
    },
    setSelectedUnifierDestinationTableId: (state, action) => {
      if (Array.isArray(action.payload)) {
        //@ts-ignore
        state.selectedUnifierDestinationTableId = action.payload;
        return;
      }
      const { destinationTableId, configName, checked } = action.payload;

      if (checked) {
        const exists = state.selectedUnifierDestinationTableId.some(
          (c: any) => c.destinationTableId === destinationTableId
        );
        if (!exists) {
          //@ts-ignore
          state.selectedUnifierDestinationTableId.push({
            destinationTableId,
            configName,
          });
        }
      } else {
        state.selectedUnifierDestinationTableId =
          state.selectedUnifierDestinationTableId.filter(
            (c: any) => c.destinationTableId !== destinationTableId
          );
      }
    },
  },
});

export const {
  setSelectedDataCollectionBrands,
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setOpenModalType,
  setSelectedInterface,
  setSelectedBrands,
  setSelectedAccount,
  setSelectedCompany,
  setSelectedWebsites,
  setSelectedStatus,
  setSelectedRawTableId,
  setSort,
  setDataCollectionBrandsList,
  setSingleRowData,
  setIsLoading,
  setIsSuccess,
  setModalOpen,
  setStartDate,
  setEndDate,
  setSelectedAction,
  setSelectedActionId,
  setSelectedBrandConfigs,
  setSelectedUnifierDestinationTableId,
  setResWorkflowName,
} = dataCollectionBrandsSlice.actions;

export default dataCollectionBrandsSlice.reducer;
